import type { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { Box, InputBase, Tooltip } from '@mui/material';

import Icon from '../../components/Icon';
import TagPill from '../../components/TagPill';
import { ICONS } from '../../shared/icons.generated';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import type { VocabularyEntry } from '../../shared/opportunity';
import {
  SWITCHES,
  TIME_SLOTS,
  WEEKDAYS,
  type SwitchKey,
  type TimeSlot,
  type Weekday,
} from '../../shared/taxonomy';
import type { FacetCounts, FilterState } from './filters';

/**
 * The filter sidebar.
 *
 * MCW's controls with the demo's facets: seven weekdays, time of day, six
 * roles, ten topics, the switches and the three pools. Every option carries
 * its count (`facetCounts`: what choosing it would show, never counted against
 * its own facet), and an option nothing would match stays, dimmed.
 */

const Section = styled('section')({
  marginBottom: 30,
  // The day, time and intent boxes are sized against the section's own width.
  containerType: 'inline-size',
});

const SectionHead = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 12,
});

const SectionTitle = styled('h2')({
  margin: 0,
  fontFamily: FONTS.display,
  // Cy Text Bold, per the brand guidelines for section headers.
  fontWeight: 700,
  fontSize: 21,
  letterSpacing: '-0.005em',
  color: BRAND.parchment,
});

const ResetLink = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 12,
  fontWeight: 700,
  color: 'rgba(243,238,231,0.6)',
  '&:hover': { color: BRAND.parchment },
  '&:disabled': { opacity: 0.3, cursor: 'default' },
});

const SearchWrap = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  border: `1px solid rgba(243,238,231,0.5)`,
  borderRadius: 999,
  padding: '9px 16px',
  marginBottom: 18,
  transition: 'border-color 120ms ease',
  '&:focus-within': { borderColor: BRAND.parchment },
});

const Count = styled('span')({
  fontFamily: FONTS.body,
  fontSize: 10,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  opacity: 0.62,
  marginLeft: 'auto',
  paddingLeft: 6,
});

/**
 * A section's Reset, sized to sit on the heading's baseline. With nothing to
 * reset it is the reference's quiet grey rather than faded out; with something
 * to reset it is full parchment.
 */
const SectionReset = styled(ResetLink)({
  fontSize: 16,
  color: BRAND.parchment,
  transition: 'color 160ms ease',
  '&:hover': { color: BRAND.parchment, opacity: 0.8 },
  '&:disabled': { opacity: 1, color: 'rgba(243,238,231,0.6)' },
});

/**
 * The day, time-of-day and intent filters, after the filter reference.
 *
 * Its measurements are for a 460px-wide section, the desktop sidebar's natural
 * width. `cq` turns one of them into a share of the section's width, so the
 * same proportions hold in the tablet drawer and on a phone, and `fluid` does
 * the same for a font size without letting it drop below a readable floor.
 */
const cq = (px: number) => `${Number((px / 4.6).toFixed(3))}cqw`;
const fluid = (px: number, min: number) => `max(${min}px, ${cq(px)})`;

/** The labels outside the boxes: weekdays, hours and intents. */
const QUIET = 'rgba(243,238,231,0.85)';

/**
 * The icon artwork is drawn at one scale for every icon of a kind rather than
 * fitted to a square, so a wide handshake stays wider than a tall glass. `per`
 * is px of icon per unit of viewBox width, at the reference width.
 */
function iconWidth(name: string, per: number): string {
  const viewBox = ICONS[name]?.viewBox.split(' ').map(Number);
  return cq((viewBox?.[2] ?? 0) * per);
}

/** An intent label on two balanced lines, the longer first when they tie. */
function twoLines(label: string): [string, string] | [string] {
  const words = label.split(' ');
  if (words.length < 2) return [label];
  let best: [string, string] = [label, ''];
  let longest = Infinity;
  for (let i = 1; i < words.length; i++) {
    const first = words.slice(0, i).join(' ');
    const second = words.slice(i).join(' ');
    const length = Math.max(first.length, second.length);
    if (length <= longest) {
      best = [first, second];
      longest = length;
    }
  }
  return best;
}

/**
 * One choice: the box, plus the words that sit outside it, all one button so
 * the words are as clickable as the box. Hovering lifts an unselected box's
 * fill from 6% to 20%.
 */
const Choice = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  minWidth: 0,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  font: 'inherit',
  color: BRAND.parchment,
  '@media (hover: hover)': {
    '&:hover > [data-box="off"]': { backgroundColor: 'rgba(243,238,231,0.2)' },
  },
  '&:focus-visible': { outline: 'none' },
  '&:focus-visible > [data-box]': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: 2 },
});

/** Unselected: a thin parchment outline around a 6% parchment fill. Selected: solid parchment. */
const ChoiceBox = styled('span', { shouldForwardProp: (p) => p !== '$on' })<{ $on: boolean }>(({ $on }) => ({
  position: 'relative',
  display: 'grid',
  placeItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
  border: `1px solid ${$on ? BRAND.parchment : 'rgba(243,238,231,0.75)'}`,
  backgroundColor: $on ? BRAND.parchment : 'rgba(243,238,231,0.06)',
  color: $on ? BRAND.mocha : BRAND.parchment,
  transition: 'background-color 220ms ease, color 220ms ease, border-color 220ms ease',
}));

const DayGrid = styled('div')({
  marginTop: 9,
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  columnGap: cq(6.8),
});

const DayBox = styled(ChoiceBox)({
  aspectRatio: '122 / 162',
  borderRadius: cq(7.8),
});

const DayNumber = styled('span')({
  fontFamily: FONTS.condensed,
  fontWeight: 700,
  fontSize: fluid(24, 20),
  lineHeight: 1,
});

const SlotGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  columnGap: cq(30.6),
  marginTop: cq(16.3),
});

const SlotBox = styled(ChoiceBox)({
  display: 'block',
  aspectRatio: '281 / 258',
  borderRadius: cq(9.8),
});

/** Centred at these heights in the box, whatever the artwork's proportions. */
const SlotIcon = styled('span')({
  position: 'absolute',
  left: '50%',
  top: '39%',
  display: 'block',
  transform: 'translate(-50%, -50%)',
  '& > svg': { width: '100%', height: 'auto' },
});

const SlotName = styled('span')({
  position: 'absolute',
  left: '50%',
  top: '80%',
  transform: 'translate(-50%, -50%)',
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: fluid(12.5, 11),
  lineHeight: 1,
  whiteSpace: 'nowrap',
});

const SlotHint = styled('span')({
  marginTop: cq(6),
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: fluid(11, 10),
  lineHeight: 1.3,
  color: QUIET,
  whiteSpace: 'nowrap',
});

/** One row of six once the section is about the reference's width; two rows of three below that. */
const IntentGrid = styled('div')({
  marginTop: 5,
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  columnGap: cq(10.1),
  rowGap: 10,
  '& [data-box]': { height: cq(92) },
  '@container (min-width: 440px)': {
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    '& [data-box]': { height: 'auto', aspectRatio: '209 / 226' },
  },
});

const IntentBox = styled(ChoiceBox)({
  borderRadius: cq(7.8),
  '& > span': { display: 'block' },
  '& > span > svg': { width: '100%', height: 'auto' },
});

/** Always two lines tall, so one-line labels sit centred beside two-line ones. */
const IntentLabel = styled('span')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '2.3em',
  marginTop: cq(4),
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: fluid(11, 10.5),
  lineHeight: 1.15,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  color: QUIET,
});

/** A bare button so TagPill can own the entire appearance. */
const TagButton = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  width: '100%',
  display: 'block',
  textAlign: 'left',
  cursor: 'pointer',
});

/** A labelled on/off switch for the two toggles that widen what Explore lists. */
const ToggleRow = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  width: '100%',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 0',
  textAlign: 'left',
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 600,
  color: BRAND.parchment,
  '&:focus-visible': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: 2, borderRadius: 6 },
});

const Switch = styled('span', { shouldForwardProp: (p) => p !== '$on' })<{ $on: boolean }>(({ $on }) => ({
  position: 'relative',
  flexShrink: 0,
  width: 34,
  height: 20,
  borderRadius: 999,
  background: $on ? BRAND.parchment : 'transparent',
  border: `1px solid ${$on ? BRAND.parchment : 'rgba(243,238,231,0.45)'}`,
  transition: 'background 140ms ease, border-color 140ms ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 2,
    left: $on ? 16 : 2,
    width: 14,
    height: 14,
    borderRadius: '50%',
    background: $on ? BRAND.mocha : 'rgba(243,238,231,0.7)',
    transition: 'left 160ms ease, background 140ms ease',
  },
}));

/** A count in a box's bottom-right corner: muted, and never in the way of the label. */
const BoxCount = styled('span')({
  position: 'absolute',
  right: cq(7),
  bottom: cq(5),
  fontFamily: FONTS.body,
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums',
  opacity: 0.6,
});

/** Under a weekday's name, inside its box. */
const DayCount = styled('span')({
  marginTop: cq(6),
  fontFamily: FONTS.body,
  fontSize: fluid(11, 10),
  fontWeight: 700,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums',
  opacity: 0.65,
});

const DayInner = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

/** An option nothing would match: still there, still tappable (to turn it off), but quiet. */
const EMPTY_OPTION = {
  '&[data-empty="true"]': { opacity: 0.42 },
} as const;

/** Under a switch that is on: how many listings it left out for want of data. */
const SwitchNote = styled('div')({
  margin: '-3px 0 6px 44px',
  fontFamily: FONTS.body,
  fontSize: 11.5,
  fontWeight: 500,
  color: 'rgba(243,238,231,0.6)',
});

const SwitchHelp = styled('div')({
  margin: '-3px 0 6px 44px',
  fontFamily: FONTS.body,
  fontSize: 11.5,
  lineHeight: 1.4,
  fontWeight: 500,
  color: 'rgba(243,238,231,0.6)',
});

interface SidebarProps {
  filters: FilterState;
  /** From the city file's taxonomy, in its order. */
  topics: VocabularyEntry[];
  roles: VocabularyEntry[];
  /** Beside every option: how many events choosing it would show (`facetCounts`). */
  counts: FacetCounts;
  /** Per switch that is on: listings it left out because they don't say. */
  unknownExcluded: Partial<Record<SwitchKey, number>>;
  onQuery: (q: string) => void;
  onToggleDay: (key: Weekday) => void;
  onToggleSlot: (slot: TimeSlot) => void;
  onToggleRole: (key: string) => void;
  onToggleTopic: (key: string) => void;
  onToggleSwitch: (key: SwitchKey) => void;
  onResetFacet: (facet: 'weekdays' | 'slots' | 'roles' | 'topics' | 'switches') => void;
  onResetAll: () => void;
  onToggleShowHidden: () => void;
  onToggleShowPast: () => void;
  /** The travel-time section (`travel/DistanceSection`), drawn between the days and the roles. */
  distance?: ReactNode;
  onResetDistance?: () => void;
}

const listings = (n: number) => `${n} ${n === 1 ? 'listing doesn\u2019t' : 'listings don\u2019t'} say.`;

export default function Sidebar({
  filters,
  topics,
  roles,
  counts,
  unknownExcluded,
  onQuery,
  onToggleDay,
  onToggleSlot,
  onToggleRole,
  onToggleTopic,
  onToggleSwitch,
  onResetFacet,
  onResetAll,
  onToggleShowHidden,
  onToggleShowPast,
  distance,
  onResetDistance,
}: SidebarProps) {
  const pools = [
    // Saved is the Scope pill now (contract §10), not a switch here.
    { key: 'hidden', label: 'Show events marked \u201cNot Interested\u201d', on: filters.showHidden, count: counts.showHidden, toggle: onToggleShowHidden },
    { key: 'past', label: 'Show past events', on: filters.showPast, count: counts.showPast, toggle: onToggleShowPast },
  ];

  return (
    <Box sx={{ pb: 8 }}>
      <SearchWrap>
        <SearchGlyph />
        <InputBase
          value={filters.query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search for events..."
          inputProps={{ 'aria-label': 'Search events' }}
          sx={{
            flex: 1,
            fontFamily: FONTS.body,
            fontSize: 14,
            color: BRAND.parchment,
            '& input::placeholder': { color: 'rgba(243,238,231,0.5)', opacity: 1 },
          }}
        />
        {filters.query && (
          <ResetLink onClick={() => onQuery('')} aria-label="Clear search">
            ✕
          </ResetLink>
        )}
      </SearchWrap>

      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <ResetLink onClick={onResetAll} style={{ fontSize: 13, color: BRAND.parchment }}>
          Reset all filters
        </ResetLink>
      </Box>

      {/* ---------------- days ---------------- */}
      <Section style={{ marginBottom: 33 }}>
        <SectionHead>
          <SectionTitle>Filter by day of the week</SectionTitle>
          <SectionReset onClick={() => onResetFacet('weekdays')} disabled={filters.weekdays.size === 0}>
            Reset
          </SectionReset>
        </SectionHead>

        <DayGrid>
          {WEEKDAYS.map((d) => {
            const on = filters.weekdays.has(d.key);
            const count = counts.weekdays[d.key] ?? 0;
            return (
              <Choice
                key={d.key}
                type="button"
                aria-pressed={on}
                aria-label={`${d.weekday}, ${count} ${count === 1 ? 'event' : 'events'}`}
                data-empty={count === 0 && !on}
                sx={EMPTY_OPTION}
                onClick={() => onToggleDay(d.key)}
              >
                <DayBox data-box={on ? 'on' : 'off'} $on={on}>
                  <DayInner>
                    <DayNumber>{d.label}</DayNumber>
                    <DayCount aria-hidden="true">{count}</DayCount>
                  </DayInner>
                </DayBox>
              </Choice>
            );
          })}
        </DayGrid>

        <SlotGrid>
          {TIME_SLOTS.map((slot) => {
            const on = filters.slots.has(slot.id);
            const name = `time:${slot.id}`;
            const count = counts.slots[slot.id] ?? 0;
            return (
              <Choice
                key={slot.id}
                type="button"
                aria-pressed={on}
                aria-label={`${slot.label}, ${slot.hint}, ${count} ${count === 1 ? 'event' : 'events'}`}
                data-empty={count === 0 && !on}
                sx={EMPTY_OPTION}
                onClick={() => onToggleSlot(slot.id)}
              >
                <SlotBox data-box={on ? 'on' : 'off'} $on={on}>
                  {/* One scale for all four: a sunrise is wider than a moon. */}
                  <SlotIcon style={{ width: iconWidth(name, 0.059) }}>
                    <Icon name={name} size="100%" />
                  </SlotIcon>
                  <SlotName>{slot.label}</SlotName>
                  {/* Top right: the slot's name has the bottom. */}
                  <BoxCount aria-hidden="true" style={{ bottom: 'auto', top: cq(7) }}>
                    {count}
                  </BoxCount>
                </SlotBox>
                <SlotHint aria-hidden="true">({slot.hint})</SlotHint>
              </Choice>
            );
          })}
        </SlotGrid>
      </Section>

      {/* ---------------- travel time ---------------- */}
      {distance && (
        <Section data-slot="distance">
          <SectionHead>
            <SectionTitle>Filter by travel time</SectionTitle>
            <SectionReset onClick={onResetDistance} disabled={filters.withinMinutes == null}>
              Reset
            </SectionReset>
          </SectionHead>
          {distance}
        </Section>
      )}

      {/* ---------------- I want to be a ---------------- */}
      <Section>
        <SectionHead>
          <SectionTitle>I want to be a...</SectionTitle>
          <SectionReset onClick={() => onResetFacet('roles')} disabled={filters.roles.size === 0}>
            Reset
          </SectionReset>
        </SectionHead>
        <IntentGrid>
          {roles.map((role) => {
            const on = filters.roles.has(role.key);
            const [first, second] = twoLines(role.label);
            const count = counts.roles[role.key] ?? 0;
            return (
              <Tooltip key={role.key} title={role.subtext ?? ''} placement="top" arrow disableInteractive>
                <Choice
                  type="button"
                  aria-pressed={on}
                  aria-label={`${role.label}, ${count} ${count === 1 ? 'event' : 'events'}`}
                  aria-description={role.subtext}
                  data-empty={count === 0 && !on}
                  sx={EMPTY_OPTION}
                  onClick={() => onToggleRole(role.key)}
                >
                  <IntentBox data-box={on ? 'on' : 'off'} $on={on}>
                    <span style={{ width: '55%' }}>
                      <Icon name={`role:${role.key}`} emoji={role.icon} size="100%" />
                    </span>
                    <BoxCount aria-hidden="true">{count}</BoxCount>
                  </IntentBox>
                  <IntentLabel aria-hidden="true">
                    <span>
                      {first}
                      {second && (
                        <>
                          <br />
                          {second}
                        </>
                      )}
                    </span>
                  </IntentLabel>
                </Choice>
              </Tooltip>
            );
          })}
        </IntentGrid>
      </Section>

      {/* ---------------- topics ---------------- */}
      <Section>
        <SectionHead>
          <SectionTitle>I'm interested in...</SectionTitle>
          <SectionReset onClick={() => onResetFacet('topics')} disabled={filters.topics.size === 0}>
            Reset
          </SectionReset>
        </SectionHead>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.8 }}>
          {topics.map((topic) => {
            const on = filters.topics.has(topic.key);
            const count = counts.topics[topic.key] ?? 0;
            return (
              <TagButton
                key={topic.key}
                aria-pressed={on}
                aria-label={`${topic.label}, ${count} ${count === 1 ? 'event' : 'events'}`}
                data-empty={count === 0 && !on}
                sx={EMPTY_OPTION}
                onClick={() => onToggleTopic(topic.key)}
              >
                <TagPill topic={topic} on={on} count={count} />
              </TagButton>
            );
          })}
        </Box>
      </Section>

      {/* ---------------- switches ---------------- */}
      <Section>
        <SectionHead>
          <SectionTitle>Other filters</SectionTitle>
          <SectionReset onClick={() => onResetFacet('switches')} disabled={filters.switches.size === 0}>
            Reset
          </SectionReset>
        </SectionHead>
        {SWITCHES.map((f) => {
          const on = filters.switches.has(f.key);
          const count = counts.switches[f.key] ?? 0;
          const unknown = unknownExcluded[f.key] ?? 0;
          return (
            <Box key={f.key}>
              <ToggleRow
                type="button"
                role="switch"
                aria-checked={on}
                data-empty={count === 0 && !on}
                sx={EMPTY_OPTION}
                onClick={() => onToggleSwitch(f.key)}
              >
                <Switch $on={on} aria-hidden="true" />
                <span>{f.label}</span>
                <Count>{count}</Count>
              </ToggleRow>
              {f.key === 'under18' && (
                <SwitchHelp>Shows listings open to under-18s, and flags the ones that don't say.</SwitchHelp>
              )}
              {on && unknown > 0 && <SwitchNote>{listings(unknown)}</SwitchNote>}
            </Box>
          );
        })}
      </Section>

      {/* ---------------- what the list runs over ---------------- */}
      <Box>
        {pools.map((pool) => (
          <ToggleRow key={pool.key} type="button" role="switch" aria-checked={pool.on} onClick={pool.toggle}>
            <Switch $on={pool.on} aria-hidden="true" />
            <span>{pool.label}</span>
            <Count>{pool.on ? '' : pool.count}</Count>
          </ToggleRow>
        ))}
      </Box>
    </Box>
  );
}

function SearchGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        d="M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm5.5 12.5L21 21"
      />
    </svg>
  );
}
